/**
 * Dialog zum Anlegen und Bearbeiten eines Anlagentyps.
 *
 * Aufbau wie beim Anlagen-Dialog: ein aufgeklappter Kopf mit Hersteller, Typ und den technischen
 * Grunddaten, darunter die vereinbarten Abschnitte als eingeklappte Akkordeons. Die Abschnitte
 * sind dieselben Komponenten, die später die Anlage im Projekt benutzt – nur ohne die Angaben,
 * die es je Anlage gibt: Richtfunk, Einsatzzeiten, Flächenbilanz.
 *
 * Beim **Anlegen** zeigen Stoffliste und Betriebsmodi statt der Tabelle einen Hinweis: Kindzeilen
 * brauchen eine anlagentyp_id, und die entsteht erst mit dem Speichern.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import DialogTemplate, { DialogSection } from "../../../layout/DialogTemplate";
import Button from "../../../common/inputs/Button";
import InputField from "../../../common/inputs/InputField";
import InputFieldNumber from "../../../common/inputs/InputFieldNumber";
import Dropdown from "../../../common/inputs/Dropdown";
import AusstattungFelder from "../../projectGenerationUnits/components/AusstattungFelder";
import WasserUndKlimaschutzFelder from "../../projectGenerationUnits/components/WasserUndKlimaschutzFelder";
import StoffListeSection from "../../projectGenerationUnits/components/StoffListeSection";
import BetriebsmodusListeSection from "../../projectGenerationUnits/components/BetriebsmodusListeSection";
import { useWertelisten } from "../../projectGenerationUnits/hooks/useWertelisten";
import { useAnlagentypListen } from "../hooks/useAdminAnlagentypen";
import {
  Anlagentyp,
  AnlagentypCreate,
  AnlagentypFelder,
  AnlagentypUpdate,
  Hersteller,
} from "../../../../services/api/anlagentypen";

type Werte = Partial<AnlagentypFelder>;

const LEER: Werte = {};

const HINWEIS_ANLEGEN =
  "Steht zur Verfügung, sobald der Anlagentyp gespeichert ist – die Einträge hängen an ihm.";

interface AnlagentypDialogProps {
  open: boolean;
  /** Vorhandener Anlagentyp beim Bearbeiten, `null` beim Anlegen */
  anlagentyp: Anlagentyp | null;
  hersteller: Hersteller[];
  /** Beim Anlegen vorbelegter Hersteller – der im Abschnitt darüber ausgewählte */
  vorauswahlHerstellerId?: string | null;
  onClose: () => void;
  onCreate: (daten: AnlagentypCreate) => Promise<Anlagentyp>;
  onUpdate: (id: string, daten: AnlagentypUpdate) => Promise<void>;
}

const Raster: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
      gap: 2,
      mt: 1,
    }}
  >
    {children}
  </Box>
);

const AnlagentypDialog: React.FC<AnlagentypDialogProps> = ({
  open,
  anlagentyp,
  hersteller,
  vorauswahlHerstellerId,
  onClose,
  onCreate,
  onUpdate,
}) => {
  const [form, setForm] = useState<Werte>(LEER);
  const [herstellerId, setHerstellerId] = useState("");
  const [saving, setSaving] = useState(false);
  const [fehler, setFehler] = useState("");

  const { wertelisten } = useWertelisten();
  const listen = useAnlagentypListen(anlagentyp?.id ?? null);

  useEffect(() => {
    if (open) {
      setForm(anlagentyp ? { ...anlagentyp } : LEER);
      setHerstellerId(
        anlagentyp?.hersteller_id ?? vorauswahlHerstellerId ?? "",
      );
      setFehler("");
    }
  }, [open, anlagentyp, vorauswahlHerstellerId]);

  const setzen = useCallback(
    (feld: string, wert: string | number | boolean | null) =>
      setForm((vorher) => ({ ...vorher, [feld]: wert })),
    [],
  );

  const zahl = (feld: keyof Werte) => {
    const wert = form[feld];
    return wert === null || wert === undefined ? "" : String(wert);
  };

  const speichern = async () => {
    if (!herstellerId) {
      setFehler("Hersteller und Typ identifizieren den Anlagentyp.");
      return;
    }
    if (!form.typ?.trim()) {
      setFehler("Ohne Typbezeichnung ist der Anlagentyp nicht auffindbar.");
      return;
    }
    setSaving(true);
    try {
      if (anlagentyp) {
        await onUpdate(anlagentyp.id, form);
      } else {
        await onCreate({
          ...form,
          hersteller_id: herstellerId,
          typ: form.typ.trim(),
        });
      }
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const imAnlegen = !anlagentyp;

  return (
    <DialogTemplate
      open={open}
      onClose={onClose}
      title={anlagentyp ? "Modell bearbeiten" : "Modell anlegen"}
      maxWidth="lg"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={speichern} disabled={saving}>
            Speichern
          </Button>
        </>
      }
    >
      <DialogSection title="Anlagentyp" first>
        <Raster>
          <Dropdown
            label="Hersteller"
            value={herstellerId}
            options={hersteller.map((h) => ({ value: h.id, label: h.name }))}
            onChange={setHerstellerId}
            // Der Hersteller wechselt nicht: Das machte den Anlagentyp zu einem anderen, und
            // Anlagen, die ihn kopiert haben, zeigten auf eine Vorlage, die es so nie gab.
            disabled={!imAnlegen}
            variant="floating"
            error={!!fehler && !herstellerId}
          />
          <InputField
            label="Typ"
            value={form.typ ?? ""}
            onChange={(v) => {
              setzen("typ", v);
              if (fehler) setFehler("");
            }}
            error={fehler && herstellerId ? fehler : undefined}
            required
            fullWidth
            placeholder="z. B. V162-6.2"
          />
          <InputFieldNumber
            label="Rotorradius"
            value={zahl("rotorradius")}
            onChange={(v) => setzen("rotorradius", v)}
            suffix="m"
            variant="floating"
          />
          <InputFieldNumber
            label="Nabenhöhe"
            value={zahl("nabenhöhe")}
            onChange={(v) => setzen("nabenhöhe", v)}
            suffix="m"
            variant="floating"
          />
          <InputFieldNumber
            label="Nennleistung"
            value={zahl("nennleistung")}
            onChange={(v) => setzen("nennleistung", v)}
            suffix="MW"
            variant="floating"
            tooltip="Die Einheit ist MW, nicht kW – so steht es im ausgelegten Antrag zu ELiA 16.1.9."
          />
          <InputFieldNumber
            label="Referenzertrag"
            value={zahl("referenzertrag")}
            onChange={(v) => setzen("referenzertrag", v)}
            suffix="MWh"
            variant="floating"
          />
          <InputFieldNumber
            label="Sicherungsradius"
            value={zahl("sicherungsradius")}
            onChange={(v) => setzen("sicherungsradius", v)}
            suffix="m"
            variant="floating"
          />
        </Raster>
      </DialogSection>

      <StoffListeSection
        stoffe={listen.stoffe}
        wertelisten={wertelisten}
        loading={listen.loading}
        hinweisStattListe={imAnlegen ? HINWEIS_ANLEGEN : undefined}
        onCreate={listen.createStoff}
        onUpdate={listen.updateStoff}
        onDelete={listen.deleteStoff}
      />

      <BetriebsmodusListeSection
        modi={listen.betriebsmodi}
        wertelisten={wertelisten}
        loading={listen.loading}
        hinweisStattListe={imAnlegen ? HINWEIS_ANLEGEN : undefined}
        onCreate={listen.createBetriebsmodus}
        onUpdate={listen.updateBetriebsmodus}
        onDelete={listen.deleteBetriebsmodus}
      />

      <WasserUndKlimaschutzFelder
        werte={form}
        onChange={setzen}
        wertelisten={wertelisten}
      />

      {/* Ohne Richtfunk: der haengt am Standort der einzelnen Anlage, nicht am Typ. */}
      <AusstattungFelder werte={form} onChange={setzen} />
    </DialogTemplate>
  );
};

export default AnlagentypDialog;
